import type { ThemeDefinition } from "./types";

/**
 * Single source of truth for vendor app themes.
 *
 * Consumed by three parties:
 *  - the SPA (ThemeProvider / slots resolver) at runtime,
 *  - the worker (validate + inject palette, font stack, @font-face, preloads),
 *  - the manager's theme picker via a mirrored static list (apps cannot
 *    import from each other; ids are stable strings).
 *
 * This file must stay dependency-free (no React/DOM imports): the worker
 * bundles it into its own edge runtime.
 *
 * Adding a theme = one folder under src/themes/<id>/ + one entry here
 * + one card in the manager picker (VendorThemePicker).
 */

const BRAND_DEFAULT_FONT_STACK =
  "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";

export const THEMES = {
  default: {
    id: "default",
    label: "Default",
    font: null,
    fontStack: BRAND_DEFAULT_FONT_STACK,
    palette: {},
    themeColor: "#000000",
    backgroundColor: "#ffffff",
  },
  classic: {
    id: "classic",
    label: "Classic",
    font: null,
    fontStack: BRAND_DEFAULT_FONT_STACK,
    palette: {
      "--theme-surface": "#faf7f2",
      "--theme-accent": "#b08d4f",
    },
    themeColor: "#211d19",
    backgroundColor: "#faf7f2",
  },
  fluid: {
    id: "fluid",
    label: "Fluid",
    font: null,
    fontStack: BRAND_DEFAULT_FONT_STACK,
    palette: {
      "--theme-surface": "#f7f8fa",
      "--theme-accent": "#6366f1",
    },
    themeColor: "#6366f1",
    backgroundColor: "#f7f8fa",
  },
} as const satisfies Record<string, ThemeDefinition>;

export type ThemeId = keyof typeof THEMES;

export type { ThemeDefinition };

export const resolveTheme = (theme?: string | null): ThemeId =>
  theme && theme in THEMES ? (theme as ThemeId) : "default";

export const getTheme = (theme?: string | null): ThemeDefinition =>
  THEMES[resolveTheme(theme)];
